import api from "api";
import ApiCaller from "api/apiCaller";
import {DataPipeline, DataSourceFilter, DataPipelineTemplateFilter, DataPipelinesListingResponse} from "model/data_pipeline";
import React, { useState, useEffect } from "react";
import { UserInfo } from "model/user";
import {
  Button,
  Col,
  Row,
  Container,
  Tooltip,
  Table
} from "reactstrap";
import CreatePipelineSelectionModal from './components/CreatePipelineSelectionModal';
import PipelineConfigurationModal from './components/DataPipelineConfigurationModal'
import DataPipelineItem from './components/DataPipelineItem'
import "./index.css";
import Loading from "components/Loading";
import ReactPaginate from "react-paginate";
import Select, { ValueType } from "react-select";


const DEFAULT_PAGE_LIMIT = 25;
interface ListingData {
  pipelines: Array<DataPipeline>;
  total: number;
  data_sources_filter: Array<DataSourceFilter>;
  pipeline_templates_filter: Array<DataPipelineTemplateFilter>;
  running_pipelines: number;
  success_pipelines: number;
  failed_pipelines: number;
  not_started_pipelines: number;
  skipped_pipelines: number;
}

type OptionType = { value: string, label: string };

export default function DataPipelineListing() {
  const companyId: any = api.getCurrentCompanyId();
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null)
  const [listingData, setListingData] = useState<ListingData>({
    pipelines: [],
    total: 0,
    data_sources_filter: [],
    pipeline_templates_filter: [],
    running_pipelines: 0,
    success_pipelines: 0,
    failed_pipelines: 0,
    not_started_pipelines: 0,
    skipped_pipelines: 0
  });
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageLimit, setPageLimit] = useState<number>(DEFAULT_PAGE_LIMIT);
  const pageLimitOptions = [
    { 'value': 25, 'label': '25'},
    { 'value': 50, 'label': '50'},
    { 'value': 75, 'label': '75'},
    { 'value': 100, 'label': '100'},
  ]

  const [tooltipState, setTooltipState] = useState<any>({});
  const toggleTooltip = (target: string) => {
    if (!tooltipState[target]) {
      setTooltipState({
        ...tooltipState,
        [target]: { tooltipOpen: true }
      });
    } else {
      setTooltipState({
        ...tooltipState,
        [target]: { tooltipOpen: !tooltipState[target].tooltipOpen }
      });
    }
  };

  const isTooltipOpen = (target: string) => {
    return tooltipState[target] ? tooltipState[target].tooltipOpen : false;
  };

  const [sortType, setSortType] = useState<string>('desc');
  const sortTypeOptions = [
    { 'value': 'asc', 'label': 'Ascending'},
    { 'value': 'desc', 'label': 'Descending'},
  ];

  const [sortBy, setSortBy] = useState<string>('last_run');
  const sortByOptions = [
    { 'value': 'created_on', 'label': 'Date Created'},
    { 'value': 'last_run', 'label': 'Last Run'},
  ];

  const [filterStatus, setFilterStatus] = useState<string>('');
  const filterStatusOptions = [
    { 'value': 'running', 'label': 'Running'},
    { 'value': 'success', 'label': 'Success'},
    { 'value': 'failed', 'label': 'Failed'},
    { 'value': 'not_started', 'label': 'Not started'},
    { 'value': 'skipped', 'label': 'Skipped'}
  ];

  const [filterDatasource, setFilterDatasource] = useState<string>('');
  const [filterTemplate, setFilterTemplate] = useState<string>('');

  const handleFilterStatusChange = (selectedStatus: ValueType<OptionType>) => {
    const statuses = (selectedStatus as OptionType[])?.map((item: OptionType) => item.value);
    if (statuses !== undefined) {
      setFilterStatus(statuses.join(","));
    } else {
      setFilterStatus('')
    }
    setPage(0);
  };

  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const toggleCreateModal = () => {
    setShowSelectionModal(!showSelectionModal);
  };

  const [showPipelineConfiguration, setShowPipelineConfiguration] = useState(false);
  const togglePipelineConfiguration = () => {
    setShowPipelineConfiguration(!showPipelineConfiguration);
  };

  const dataPipelineAdmin = (Object: { name: string; }) => Object.name === 'Data Pipeline Admin' || Object.name === 'Admin';
  const isDataPipelineAdmin = currentUser?.roles.some(dataPipelineAdmin);

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
  };

  const getDataPipelines = async () => {
    const res:any = await ApiCaller.get(
      `pipeline/?company_id=${companyId}&is_active=1&limit=${pageLimit}&page=${page}` +
      `&sort_by=${sortBy}&sort_type=${sortType}&status=${filterStatus}` +
      `&source_datasource_id=${filterDatasource}&data_pipeline_template_id=${filterTemplate}`
    );
    const data: DataPipelinesListingResponse = res?.data;
    if (res?.status === "ok") {
      setListingData({
        ...listingData,
        ...{
          pipelines: data?.pipelines,
          total: data?.total,
          data_sources_filter: data?.data_sources_filter,
          pipeline_templates_filter: data?.pipeline_templates_filter,
          running_pipelines: data?.pipeline_statuses_stats?.running_pipelines,
          success_pipelines: data?.pipeline_statuses_stats?.success_pipelines,
          failed_pipelines: data?.pipeline_statuses_stats?.failed_pipelines,
          not_started_pipelines: data?.pipeline_statuses_stats?.not_started_pipelines,
          skipped_pipelines: data?.pipeline_statuses_stats?.skipped_pipelines
        },
      });
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    getDataPipelines();
  }, [pageLimit, page, sortBy, sortType, filterStatus, filterDatasource, filterTemplate]);

  const legends = [
    {legendName: 'running', legendLabel: 'Running', legendIcon: 'timelapse', value: listingData.running_pipelines, tooltip: 'Pipeline is running'},
    {legendName: 'success', legendLabel: 'Success', legendIcon: 'check_circle', value: listingData.success_pipelines, tooltip: 'The last run was successful! Hooray :D'},
    {legendName: 'skipped', legendLabel: 'Skipped', legendIcon: 'unpublished', value: listingData.skipped_pipelines, tooltip: 'Pipeline is OK! The last run was skipped due to no new data'},
    {legendName: 'failed', legendLabel: 'Failed', legendIcon: 'highlight_off', value: listingData.failed_pipelines, tooltip: 'The last run was failed!!!'},
    {legendName: 'not_started', legendLabel: 'Not started', legendIcon: 'not_started', value: listingData.not_started_pipelines, tooltip: 'Pipeline has not started yet'},
  ];

  if (loading) return <Loading />;

  return (
    <Container className='data-pipeline-listing__container'>
      <Row>
        <Col className="home-current-user-brand" xs={12}>
          <h3>
            {
              currentUser?.companies.find((comapny) => comapny.id == companyId)
                ?.name
            } Data Pipelines
          </h3>
        </Col>
      </Row>
      <Row>
        <Col xs={9}>
          <Button
            color="primary"
            className="data-pipeline-listing__control-buttons"
            onClick={toggleCreateModal}
          >
            <span className="material-icons">add</span>
            <span>Create new pipeline</span>
          </Button>
          <br />
        </Col>
        {isDataPipelineAdmin &&
          <Col xs={3}>
            <Button
              color="info"
              style={{'float': 'right'}}
              className="data-pipeline-listing__control-buttons"
              onClick={togglePipelineConfiguration}
            >
              <span className="material-icons">settings</span>
              <span>Configuration</span>
            </Button>
            <br />
          </Col>
        }
      </Row>
      <br />
      <Row>
        <Col xs={2} className='data-pipeline-listing__filter'>
          <Select
            options={sortByOptions}
            onChange={(selectedOption: ValueType<OptionType>) => {
                const value = (selectedOption as OptionType).value;
                setSortBy(value);
                setPage(0);
              }
            }
            placeholder='Sort By'
          />
        </Col>
        <Col xs={2} className='data-pipeline-listing__filter'>
          <Select
            options={sortTypeOptions}
            onChange={(selectedOption: ValueType<OptionType>) => {
                const value = (selectedOption as OptionType).value;
                setSortType(value);
                setPage(0);
              }
            }
            placeholder='Sort Type'
          />
        </Col>
        <Col xs={2} className='data-pipeline-listing__filter'>
          <Select
            options={listingData.data_sources_filter?.map((data_source:DataSourceFilter) => (
              { value: data_source.data_source_id, label: data_source.data_source_name }
            ))}
            onChange={(selectedOption: any) => {
              if (selectedOption) {
                setFilterDatasource(selectedOption.value);
              } else {
                setFilterDatasource('')
              }
                setPage(0);
              }
            }
            isClearable
            placeholder='Data source'
          />
        </Col>
        <Col xs={2} className='data-pipeline-listing__filter'>
          <Select
            options={listingData.pipeline_templates_filter?.map((template:DataPipelineTemplateFilter) => (
              { value: template.pipeline_template_id, label: template.pipeline_template_name }
            ))}
            onChange={(selectedOption: any) => {
              if (selectedOption) {
                setFilterTemplate(selectedOption.value);
              } else {
                setFilterTemplate('')
              }
                setPage(0);
              }
            }
            isClearable
            placeholder='Template'
          />
        </Col>
        <Col xs={3} className='data-pipeline-listing__filter'>
          <Select
            closeMenuOnSelect={false}
            onChange={handleFilterStatusChange}
            options={filterStatusOptions}
            isMulti
            placeholder={"Status"}
          />
        </Col>
        <Col xs={1} className='data-pipeline-listing__filter'>
          <Select
            options={pageLimitOptions}
            onChange={(selectedOption: any) => {
                setPageLimit(selectedOption.value);
                setPage(0);
              }
            }
            defaultValue={pageLimitOptions[0]}
            // components={{ DropdownIndicator:() => null, IndicatorSeparator:() => null }}
            menuPlacement="auto"
          />
        </Col>
      </Row>
      <Row>
        <Col xs={8} className='data-pipeline-listing__legends_box'>
          { legends.map(function(legend) {
            return (
              <div className='data-pipeline-listing__legends' key={`${legend.legendName}`}>
                <i id={legend.legendIcon} className={`material-icons icon__status__${legend.legendName}`}>{legend.legendIcon}</i>
                <span className='data-pipeline-listing__legends-name'>{legend.legendLabel}: {legend.value}</span>
                <Tooltip
                  placement="top"
                  target={legend.legendIcon}
                  isOpen={isTooltipOpen(legend.legendIcon)}
                  toggle={() => toggleTooltip(legend.legendIcon)}
                >
                  {legend.tooltip}
                </Tooltip>
              </div>
            )
          })}
        </Col>
        <Col xs={4}>
          <span></span>
          <ReactPaginate
            previousLabel={"<"}
            nextLabel={">"}
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={Math.floor(listingData?.total / pageLimit) + 1}
            marginPagesDisplayed={3}
            pageRangeDisplayed={2}
            initialPage={0}
            onPageChange={async (page) => {
              setListingData({
                ...listingData,
              });
              setPage(page.selected);
            }}
            forcePage={page}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        </Col>
      </Row>
      <Table hover responsive>
        <thead>
          <tr>
            <th>Status</th>
            <th>Name</th>
            <th>Schedule </th>
            <th>Last Run</th>
            <th>Next Run</th>
          </tr>
        </thead>
        <tbody>
        {listingData?.pipelines?.map((pipeline:DataPipeline) => {
          return (
            <DataPipelineItem key={pipeline.id} dataPipelineData={pipeline}/>
          );
        })}
        </tbody>
      </Table>
      {showSelectionModal &&
        <CreatePipelineSelectionModal
          isShow={showSelectionModal}
          onModalClose={() => setShowSelectionModal(false)}
        />
      }
      {showPipelineConfiguration &&
        <PipelineConfigurationModal
          isShow={showPipelineConfiguration}
          onModalClose={() => setShowPipelineConfiguration(false)}
        />
      }
    </Container>
  );
}
